import * as am4core from '@amcharts/amcharts4/core';
import { ForceDirectedSeries } from '@amcharts/amcharts4/plugins/forceDirected';

import { OwnershipGraphLinkResult } from '../../../../../../generated/model/ownershipGraphLinkResult';
import { OwnershipGraphResult } from '../../../../../../generated/model/ownershipGraphResult';
import {
  Icons,
  IconsType,
  OWNERSHIP_GRAPH_DEFAULT_MAX_DEPTH,
  OWNERSHIP_GRAPH_MAX_VISIBLE_SIBLINGS,
  OwnershipGraphNodeTypeEnum,
  OwnershipMappedGraphNode,
  OwnershipNodesColorEnum,
  OwnershipSyntheticIcons,
} from './OwnershipConnectionsChart.type';

interface GenerateNodesContext {
  connectedNodes: OwnershipMappedGraphNode[];
  // keyed `${fromId}_${toId}`, consumed by the chart's tooltip/stroke adapters
  mappedLinks: any;
  // explicit edge endpoints, so link ownership never has to be parsed back out of a key
  linkPairs: [string, string][];
  nodesMaxLevels: Map<string, number>;
  countryCodeToNameMap: Map<string, string>;
  includeHistoricalOwnership: boolean;
  expandedNodeIds: Set<string>;
  expandedOthersGroups: Set<string>;
  showAllConnections: boolean;
}

export function generateNodes(
  data: OwnershipGraphResult,
  countryCodeToNameMap: Map<string, string>,
  onlyBeneficialOwnership: boolean,
  includeHistoricalOwnership: boolean,
  expandedNodeIds: Set<string> = new Set(),
  expandedOthersGroups: Set<string> = new Set(),
  showAllConnections = false,
) {
  const connectedNodes: OwnershipMappedGraphNode[] = [];
  const mappedLinks: any = {};
  connectedNodes.push({
    id: data.id,
    links: data.links,
    name: data.name,
    path: Icons.COMPANY,
    fixed: true,
    color: OwnershipNodesColorEnum.MAIN_COMPANY,
    countryLabel: data.country ? countryCodeToNameMap.get(data.country) : '',
    level: 1,
  });

  const links = onlyBeneficialOwnership ? data.beneficialLinks : data.links;
  const ctx: GenerateNodesContext = {
    connectedNodes,
    mappedLinks,
    linkPairs: [],
    nodesMaxLevels: generateNodesMaxLevels(data.id!, links),
    countryCodeToNameMap,
    includeHistoricalOwnership,
    expandedNodeIds,
    expandedOthersGroups,
    showAllConnections,
  };

  const addRootBranch = (
    branchLinks: OwnershipGraphLinkResult[] | undefined,
    groupKey: string,
    startLevel: number,
  ) => {
    if (!branchLinks) {
      return;
    }

    const activeLinks = branchLinks.filter(
      (link: any) => link.active || includeHistoricalOwnership,
    );
    const displayLinks = groupSiblingsForDisplay(
      activeLinks,
      groupKey,
      expandedOthersGroups,
      showAllConnections,
    );
    displayLinks.forEach((link: any) => {
      addMappedLink(ctx, data.id!, link.id!, link);
      generateNodesInner(link, ctx, startLevel, 0, 1);
    });
  };

  addRootBranch(links, `${data.id}:owners`, 2);
  if (!onlyBeneficialOwnership) {
    addRootBranch(data.holdingLinks, `${data.id}:holdings`, 0);
  }

  return {
    nodes: connectedNodes,
    mappedLinks,
    linkPairs: ctx.linkPairs,
    // a collapsed toggle node existing is exactly what "some data is hidden" means
    hasHiddenNodes: connectedNodes.some(
      (node) => node.isSynthetic && node.expandState === 'collapsed',
    ),
  };
}

function addMappedLink(
  ctx: GenerateNodesContext,
  fromId: string,
  toId: string,
  link: OwnershipGraphLinkResult | { id: string; active: boolean },
) {
  const keyLink = `${fromId}_${toId}`;
  if (typeof ctx.mappedLinks[keyLink] === 'undefined') {
    // save all links
    ctx.mappedLinks[keyLink] = link;
  }
  ctx.linkPairs.push([fromId, toId]);
}

function generateNodesInner(
  data: OwnershipMappedGraphNode,
  ctx: GenerateNodesContext,
  level: number,
  offset: number,
  depth: number,
) {
  const { connectedNodes, nodesMaxLevels, countryCodeToNameMap } = ctx;

  // set unique connectedNodes
  const index = connectedNodes.findIndex((node) => node.id === data.id);
  if (index === -1) {
    connectedNodes.push(data);
  }

  const dataId = data.id;
  const nodeMaxLevel = nodesMaxLevels.get(dataId!) || 0;
  data.level = Math.max(nodeMaxLevel, level + offset);
  const newOffset = nodeMaxLevel - level - offset > 0 ? nodeMaxLevel - level : offset;

  if (data.isSynthetic) {
    data.fixed = true;
    data.path =
      data.expandState === 'expanded'
        ? OwnershipSyntheticIcons.COLLAPSE
        : OwnershipSyntheticIcons.EXPAND;
    data.color =
      data.isSynthetic === 'others'
        ? OwnershipNodesColorEnum.OTHERS
        : OwnershipNodesColorEnum.EXPAND;
    return;
  }

  //set icon
  let key: keyof IconsType = 'COMPANY';
  if (data.type) {
    key = data.type;
  }

  data.path = Icons[key];
  data.fixed = true;
  data.color = setNodesBackground(data);
  data.nodeValueText = data.nodeValue && data.nodeValue !== null ? `\n(${data.nodeValue}%)` : '';
  data.countryLabel =
    data.country && (!data.type || data.type === OwnershipGraphNodeTypeEnum.COMPANY)
      ? countryCodeToNameMap.get(data.country)
      : '';

  if (data.links) {
    const activeLinks = data.links.filter(
      (link: any) => link.active || ctx.includeHistoricalOwnership,
    );
    const isExpanded = ctx.expandedNodeIds.has(data.id!);
    const atDepthLimit = !ctx.showAllConnections && depth >= OWNERSHIP_GRAPH_DEFAULT_MAX_DEPTH;

    if (atDepthLimit && !isExpanded) {
      if (activeLinks.length > 0) {
        // hiddenCount is the number of direct children a click reveals, not the whole subtree
        addExpandToggleNode(
          data.id!,
          'collapsed',
          activeLinks.length,
          ctx,
          level + 1,
          newOffset,
          depth + 1,
        );
      }
      return;
    }

    const displayLinks = groupSiblingsForDisplay(
      activeLinks,
      data.id!,
      ctx.expandedOthersGroups,
      ctx.showAllConnections,
    );
    displayLinks.forEach((link: any) => {
      addMappedLink(ctx, data.id!, link.id!, link);
      generateNodesInner(link, ctx, level + 1, newOffset, depth + 1);
    });

    if (atDepthLimit && isExpanded && activeLinks.length > 0) {
      addExpandToggleNode(
        data.id!,
        'expanded',
        activeLinks.length,
        ctx,
        level + 1,
        newOffset,
        depth + 1,
      );
    }
  }
}

function addExpandToggleNode(
  expandTargetId: string,
  expandState: 'collapsed' | 'expanded',
  hiddenCount: number,
  ctx: GenerateNodesContext,
  level: number,
  offset: number,
  depth: number,
) {
  const toggleNode: OwnershipMappedGraphNode = {
    id: `expand_${expandTargetId}`,
    path: '',
    isSynthetic: 'expand',
    expandState,
    expandTargetId,
    hiddenCount,
    level: 0,
  };

  addMappedLink(ctx, expandTargetId, toggleNode.id!, { id: toggleNode.id!, active: true });
  generateNodesInner(toggleNode, ctx, level, offset, depth);
}

function comparePercentageDesc(a: any, b: any): number {
  const aValue = typeof a.nodeValue === 'number' ? a.nodeValue : -Infinity;
  const bValue = typeof b.nodeValue === 'number' ? b.nodeValue : -Infinity;
  if (aValue !== bValue) {
    return bValue - aValue;
  }
  return (a.name ?? '').localeCompare(b.name ?? '');
}

export function groupSiblingsForDisplay(
  links: OwnershipGraphLinkResult[],
  parentId: string,
  expandedOthersGroups: Set<string>,
  showAllConnections = false,
): OwnershipGraphLinkResult[] {
  if (showAllConnections || links.length <= OWNERSHIP_GRAPH_MAX_VISIBLE_SIBLINGS) {
    return links;
  }

  const sorted = [...links].sort(comparePercentageDesc);
  const visible = sorted.slice(0, OWNERSHIP_GRAPH_MAX_VISIBLE_SIBLINGS);
  const hidden = sorted.slice(OWNERSHIP_GRAPH_MAX_VISIBLE_SIBLINGS);

  const isExpanded = expandedOthersGroups.has(parentId);
  const hiddenCount = hidden.length;
  const sumValue = hidden.reduce(
    (sum, link: any) => sum + (typeof link.nodeValue === 'number' ? link.nodeValue : 0),
    0,
  );

  const othersNode: OwnershipMappedGraphNode = {
    id: `others_${parentId}`,
    path: '',
    isSynthetic: 'others',
    expandState: isExpanded ? 'expanded' : 'collapsed',
    othersGroupKey: parentId,
    hiddenCount,
    nodeValue: sumValue,
    level: 0,
  };

  return isExpanded ? [...visible, ...hidden, othersNode] : [...visible, othersNode];
}

export function generateNodesMaxLevels(rootId: string, rootLinks?: OwnershipGraphLinkResult[]) {
  const nodesMaxLevels = new Map<string, number>();
  const processNodesLevels = (
    nodeId: string,
    level: number,
    links?: OwnershipGraphLinkResult[],
  ) => {
    const nodeLevel = nodesMaxLevels.get(nodeId);
    if (nodeLevel) {
      if (level > nodeLevel) {
        nodesMaxLevels.set(nodeId, level);
      }
    } else {
      nodesMaxLevels.set(nodeId, level);
    }

    if (links) {
      links.forEach((link) => processNodesLevels(link.id!, level + 1, link.links));
    }
  };

  processNodesLevels(rootId, 1, rootLinks);
  return nodesMaxLevels;
}

export function setNodeLinks(
  connectedNodes: OwnershipMappedGraphNode[],
  linkPairs: [string, string][],
) {
  // resolved from the recorded endpoints rather than by parsing `${fromId}_${toId}` keys, which
  // breaks whenever one node id is a substring of another (including synthetic `expand_<id>` nodes)
  const linkWithById = new Map<string, string[]>();
  const add = (from: string, to: string) => {
    const linkWith = linkWithById.get(from) ?? [];
    if (linkWith.indexOf(to) === -1) {
      linkWith.push(to);
    }
    linkWithById.set(from, linkWith);
  };

  linkPairs.forEach(([from, to]) => {
    add(from, to);
    add(to, from);
  });

  connectedNodes.forEach((node: OwnershipMappedGraphNode) => {
    node.linkWith = node.id ? linkWithById.get(node.id) ?? [] : [];
  });

  return connectedNodes;
}

export function setNodeCoordinates(connectedNodes: OwnershipMappedGraphNode[]) {
  const levels = connectedNodes
    .map((item: OwnershipMappedGraphNode) => item.level)
    .filter((value, index, self) => self.indexOf(value) === index);
  if (levels.length) {
    // const heightGap = 100 / levels[levels.length - 1];
    const heightGap = 16;
    const heightStart = levels.indexOf(0) !== -1 ? 75 : 85;

    levels.forEach((level: number) => {
      const filteredNodes = connectedNodes.filter(
        (node: OwnershipMappedGraphNode) => node.level === level,
      );

      const widthGap = filteredNodes.length > 5 ? 100 / filteredNodes.length : 20;
      let widthStart = 50 - ((filteredNodes.length - 1) * widthGap) / 2;
      widthStart += -level + (level % 2) * 4;

      filteredNodes.forEach((node: OwnershipMappedGraphNode) => {
        node.x = am4core.percent(level === 1 ? 50 : widthStart);
        node.y = am4core.percent(heightStart - heightGap * (level - 1));
        widthStart += widthGap;
      });
    });
  }

  return connectedNodes;
}

export const setNodesBackground = (element: OwnershipMappedGraphNode) => {
  if (!element.type) {
    return OwnershipNodesColorEnum.MAIN_COMPANY;
  }

  switch (element.type) {
    case OwnershipGraphNodeTypeEnum.COMPANY:
      return OwnershipNodesColorEnum.COMPANY;
    case OwnershipGraphNodeTypeEnum.PERSON:
      return OwnershipNodesColorEnum.PERSON;
  }
};

export function setExportingData(series: ForceDirectedSeries) {
  series.exporting.adapter.add('data', (data) => {
    // exclude the synthetic expand/others placeholders - they are UI affordances, not ownership data
    data.data = (series.data as any[]).filter((item) => !item?.isSynthetic);
    return data;
  });
}
